import { type DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

type TrackHomeOrdersTabClickParams = {
  order: DashboardExperienceOrderResponse
  tabPosition: number
}

type TrackGetHelpWithThisBoxClickParams = {
  orderId: string
}

type TrackOrderExpandClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackRecommendedRecipeClickParams = {
  orderId: string
  collection: 'top picks' | 'limited edition'
}

type TrackingEventsForOrderTabClick = {
  action: 'click_home_orders_tab'
  properties: {
    delivery_day_id: string
    phase: string
    order_state: string
    tab_position: number
    delivery_date: string
    order_id: string
    delivery_slot_id: string
    period_id: string
    portion_size: string
  }
}

type TrackingEventsForGetHelpWithThisBoxClick = {
  action: 'click_get_help_with_this_box'
  properties: {
    order_id: string
  }
}

type TrackingEventsForOrderExpandClick = {
  action: 'order_expand_click'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type TrackingEventsForRecommendedRecipeClick = {
  action: 'click_recommended_recipe'
  properties: {
    order_id: string
    collection: 'top picks' | 'limited edition'
  }
}

export type UseOrderTracking = {
  trackHomeOrdersTabClick: (params: TrackHomeOrdersTabClickParams) => void
  trackGetHelpWithThisBoxClick: (params: TrackGetHelpWithThisBoxClickParams) => void
  trackOrderExpandClick: (params: TrackOrderExpandClickParams) => void
  trackRecommendedRecipeClick: (params: TrackRecommendedRecipeClickParams) => void
}

export const useOrderTracking = (): UseOrderTracking => {
  return {
    trackHomeOrdersTabClick: ({ order, tabPosition }: TrackHomeOrdersTabClickParams) => {
      queueTrackingEvent<TrackingEventsForOrderTabClick>({
        action: 'click_home_orders_tab',
        properties: {
          delivery_day_id: order.analytics.delivery_day_id,
          phase: order.analytics.order_phase,
          order_state: order.analytics.order_state,
          tab_position: tabPosition,
          delivery_date: order.analytics.delivery_date,
          order_id: order.analytics.order_id,
          delivery_slot_id: order.analytics.delivery_slot_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
        },
      })
    },
    trackGetHelpWithThisBoxClick: ({ orderId }: TrackGetHelpWithThisBoxClickParams) => {
      queueTrackingEvent<TrackingEventsForGetHelpWithThisBoxClick>({
        action: 'click_get_help_with_this_box',
        properties: {
          order_id: orderId,
        },
      })
    },
    trackOrderExpandClick: ({ order }: TrackOrderExpandClickParams) => {
      queueTrackingEvent<TrackingEventsForOrderExpandClick>({
        action: 'order_expand_click',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackRecommendedRecipeClick: ({ orderId, collection }: TrackRecommendedRecipeClickParams) => {
      queueTrackingEvent<TrackingEventsForRecommendedRecipeClick>({
        action: 'click_recommended_recipe',
        properties: {
          order_id: orderId,
          collection,
        },
      })
    },
  }
}
